"use client";

import type React from "react";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { supabase } from "../supabase.ts";
import { useAppTheme } from "../ThemeContext.tsx";

const Login = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define colors based on theme
  const outerBg = isDark ? "gray.800" : "gray.100";
  const innerBg = isDark ? "gray.700" : "white";
  const headingColor = isDark ? "white" : "black";
  const textColor = isDark ? "gray.300" : "gray.600";
  const inputBg = isDark ? "gray.600" : "gray.200";
  const linkColor = isDark ? "gray.300" : "gray.500";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const dashboardIconUrl = process.env.REACT_APP_DASHBOARD_ICON_URL || "";

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        toast({
          title: "Logged in successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigate("/dashboard");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An error occurred during login",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={outerBg}
    >
      <Box
        width="full"
        maxWidth="md"
        p={8}
        borderRadius="md"
        bg={innerBg}
        boxShadow="lg"
      >
        <VStack spacing={6}>
          {dashboardIconUrl && (
            <img
              src={dashboardIconUrl || "/placeholder.svg"}
              alt="Logo"
              style={{ maxHeight: "60px", marginBottom: "1rem" }}
            />
          )}
          <Heading color={headingColor}>Welcome back</Heading>
          <Text color={textColor}>Sign in to your account to continue</Text>
          <form onSubmit={handleLogin} style={{ width: "100%" }}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  bg={inputBg}
                  color={headingColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Password</FormLabel>
                <InputGroup>
                  <Input
                    id="password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    bg={inputBg}
                    color={headingColor}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      colorScheme={isDark ? "white" : "black"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <Button
                type="submit"
                colorScheme="gray"
                _hover={{ bg: isDark ? "gray.500" : "gray.300" }}
                background={isDark ? "gray.600" : "gray.200"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                width="full"
                isLoading={loading}
              >
                Sign in
              </Button>
            </VStack>
          </form>
          <Text color={textColor}>
            <Link
              to="/reset-password"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Forgot your password?
            </Link>
          </Text>
          <Text color={textColor}>
            Don't have an account?{" "}
            <Link
              to="/signup"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Sign up
            </Link>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default Login;
